import {
  ClickAwayListener,
  Collapse,
  FormControlLabel,
  Grid,
  Switch,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import {
  Close,
  Delete,
  Edit,
  Email,
  History,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  Link,
  PersonAddAlt,
  QrCodeScanner,
} from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import '../OrderBoxPopper.scss';
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import { useCalendar } from '../../CalendarContext/CalendarContext';
import { useAuth } from '../../../session/InternalAuthProvider';
import { getOrderPage } from '../../../utils/AccountUtils';
import ResponsiveDialog from '../../../common/ui/ResponsiveDialog';
import { UseAuthPermissions } from '../../../session/UseAuthPermissions';
import EditOrderDialog from '../../dialogs/EditOrderDialog/EditOrderDialog';
import SendEmailDialog from '../../dialogs/SendEmailDialog/SendEmailDialog';
import { AccountData, EnrichedEventData, OrderData } from '../../../api';
import { ParticipantsTableTabValue, TablesTabs } from './components/TablesTabs';
import CheckinDialog from '../../dialogs/CheckinDialog/CheckinDialog';
import { CheckinScannerDialog } from '../../dialogs/CheckinScannerDialog';
import CopyToClipboardButton from '../../dialogs/CopyToClipboardButton';
import { useEventOrders } from '../../swr/useEventOrders';
import {
  calculateTotalCheckedInPersons,
  calculateTotalMissingPersons,
  EventBoxDialogInfoHeader,
} from './components/EventBoxDialogInfoHeader';
import { useLanguage } from '../../../common/GeneralUtils';
import { EventActivityLog } from './components/EventActivityLog';

type EventBoxDialogProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  event: EnrichedEventData;
  openEvent: (event: EnrichedEventData) => void;
  getNextEvent: () => EnrichedEventData | undefined;
  getPreviousEvent: () => EnrichedEventData | undefined;
};
const EventBoxDialog = ({ open, setOpen, event, openEvent, getNextEvent, getPreviousEvent }: EventBoxDialogProps) => {
  const { t } = useTranslation();
  const { openDeleteEventDialog, deleteOrderDialogState, editOrderDrawerState, openExperienceAvailabilityDrawer } =
    useCalendar();
  const posthog = usePostHog();
  const [participantsTableActiveTabValue, setParticipantsTableActiveTabValue] = useState(
    ParticipantsTableTabValue.ORDERS,
  );
  const { isLoading, items: ordersList, addOrder, updateOrder } = useEventOrders({ eventId: event.id });

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { authState } = useAuth();
  const [order, setOrder] = useState<OrderData | undefined>(undefined);
  const orderPage = getOrderPage(authState.account, event.order_page_id);
  const { readOnly, isAtLeastSupervisor } = UseAuthPermissions();
  const [editOrderIsOpen, setEditOrderIsOpen] = useState(false);
  const [sendMessageDialogIsOpen, setSendMessageDialogIsOpen] = useState(false);
  const [checkInDialogIsOpen, setCheckInDialogIsOpen] = useState(false);
  const [checkInScannerOpen, setCheckInScannerOpen] = useState(false);
  const qrCheckinEnabled = Boolean((authState.account as unknown as AccountData | null)?.qr_checkin_enabled);
  const isEventToday = moment(event.date).isSame(moment(), 'day');
  const eventTime = moment(event.date).startOf('day').add(event.time, 'seconds');
  const isAtLeastOneHourBeforeEvent = moment().isAfter(eventTime.subtract(1, 'hour'));
  const [waitingListUpdateDialogIsOpen, setWaitingListUpdateDialogIsOpen] = useState(false);
  const [eventActivityLogOpen, setEventActivityLogOpen] = useState(false);
  const shouldStartInEventMode = isEventToday && isAtLeastOneHourBeforeEvent && isSmallScreen;
  // when in event we want to show only the table with no tabs or info
  const [isInEventMode, setIsInEventMode] = React.useState(shouldStartInEventMode);
  const totalCheckedInPersons = isLoading ? 0 : calculateTotalCheckedInPersons(ordersList);
  const language = useLanguage();
  const nextEvent = getNextEvent();
  const previousEvent = getPreviousEvent();
  const handleExpandClick = () => {
    setIsInEventMode(!isInEventMode);
  };
  return (
    <ResponsiveDialog
      fullScreen
      isOpen={open}
      setOpen={setOpen}
      content={
        <ClickAwayListener
          onClickAway={() => {
            if (
              !editOrderIsOpen &&
              !editOrderDrawerState.isOpen &&
              !deleteOrderDialogState.isOpen &&
              !checkInDialogIsOpen &&
              !checkInScannerOpen &&
              !waitingListUpdateDialogIsOpen &&
              !eventActivityLogOpen
            ) {
              setOpen(false);
              setEditOrderIsOpen(false);
            }
          }}>
          <Grid container flexDirection="column" flexWrap="nowrap" sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <Grid
              container
              flexDirection="column"
              px={2}
              pt={2}
              flexWrap="nowrap"
              // Header takes only what it needs; scrolls on its own if a long course roster makes
              // it outgrow its share, so it can never starve the table below it.
              sx={{ flexShrink: 0, maxHeight: '60%', overflowY: 'auto' }}>
              <Grid container flexDirection="column" justifyContent="space-between">
                <Grid item display="flex" justifyContent="flex-end" alignItems="start">
                  <Grid
                    container
                    flexDirection={language === 'he' ? 'row-reverse' : 'row-reverse'}
                    justifyContent={'flex-end'}>
                    <Tooltip title={t('next')} arrow>
                      <span>
                        <IconButton disabled={!nextEvent} onClick={() => openEvent(nextEvent!)}>
                          {language === 'he' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t('previous')} arrow>
                      <span>
                        <IconButton disabled={!previousEvent} onClick={() => openEvent(previousEvent!)}>
                          {language === 'he' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Grid>
                  {qrCheckinEnabled && ordersList.length > 0 && (
                    <Tooltip title={t('checkin_scanner.title')} arrow>
                      <span>
                        <IconButton onClick={() => setCheckInScannerOpen(true)}>
                          <QrCodeScanner />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  <Tooltip title={!isAtLeastSupervisor ? t('noPermissions') : t('add')} arrow>
                    <span>
                      <IconButton disabled={!isAtLeastSupervisor} onClick={() => setEditOrderIsOpen(true)}>
                        <PersonAddAlt />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={!isAtLeastSupervisor ? t('noPermissions') : t('calendar.event_popper.link_to_sell')}
                    arrow>
                    <span>
                      <IconButton
                        disabled={!isAtLeastSupervisor}
                        onClick={() =>
                          posthog.capture('Copy link to event', {
                            accountId: event.account_id,
                            eventId: event.id,
                          })
                        }>
                        <CopyToClipboardButton
                          IconComponent={Link}
                          text={`${import.meta.env.VITE_REACT_APP_APP_DOMAIN}/experience/${event.account_id}/${
                            event.order_page_id
                          }?event=${event.id}`}
                          orderPageId={event.order_page_id}
                        />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      !isAtLeastSupervisor
                        ? t('noPermissions')
                        : participantsTableActiveTabValue === ParticipantsTableTabValue.WAITING_LIST
                        ? t('calendar.event_popper.noWaitingListEmail')
                        : t('sendMessage')
                    }
                    arrow>
                    <span>
                      <IconButton
                        disabled={
                          !isAtLeastSupervisor ||
                          participantsTableActiveTabValue === ParticipantsTableTabValue.WAITING_LIST
                        }
                        onClick={() => setSendMessageDialogIsOpen(true)}>
                        <Email />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={readOnly ? t('noPermissions') : t('delete')} arrow>
                    <span>
                      <IconButton
                        disabled={readOnly}
                        onClick={() => {
                          openDeleteEventDialog({ event });
                          setOpen(false);
                        }}>
                        <Delete />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={!isAtLeastSupervisor ? t('noPermissions') : t('edit')} arrow>
                    <span>
                      <IconButton
                        disabled={!isAtLeastSupervisor}
                        onClick={() => {
                          openExperienceAvailabilityDrawer({ event, orderPageId: event.order_page_id });
                          setOpen(false);
                        }}>
                        <Edit />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={!isAtLeastSupervisor ? t('noPermissions') : t('auditlog.event_activity_log')} arrow>
                    <span>
                      <IconButton disabled={!isAtLeastSupervisor} onClick={() => setEventActivityLogOpen(true)}>
                        <History />
                      </IconButton>
                    </span>
                  </Tooltip>

                  <IconButton
                    onClick={() => {
                      setOpen(false);
                    }}>
                    <Tooltip title={t('close')} arrow>
                      <Close />
                    </Tooltip>
                  </IconButton>
                </Grid>
                <Grid container alignItems="center" justifyContent="space-between">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isInEventMode}
                        onChange={handleExpandClick}
                        inputProps={{ 'aria-label': 'controlled' }}
                      />
                    }
                    label={<Typography>{t('orders.eventView')}</Typography>}
                  />
                  {isInEventMode && (
                    <Grid flexDirection="column" item>
                      <Typography variant="body1" color="text.secondary">
                        {t('calendar.event_popper.total_checkeding', { total: totalCheckedInPersons })}
                      </Typography>
                      {totalCheckedInPersons > 0 && (
                        <Typography variant="body1" color="text.secondary">
                          {t('calendar.event_popper.total_missing', {
                            total: calculateTotalMissingPersons(ordersList),
                          })}
                        </Typography>
                      )}
                    </Grid>
                  )}
                </Grid>
                <Collapse in={!isInEventMode} timeout="auto" unmountOnExit>
                  <EventBoxDialogInfoHeader event={event} isLoading={isLoading} ordersList={ordersList} />
                </Collapse>
              </Grid>
            </Grid>
            <Grid
              item
              p={2}
              pt={isSmallScreen ? 0 : 2}
              sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <TablesTabs
                setCheckInDialogIsOpen={setCheckInDialogIsOpen}
                setOrder={setOrder}
                event={event}
                orderPage={orderPage}
                setEditOrderIsOpen={setEditOrderIsOpen}
                isInEventMode={isInEventMode}
                activeTab={participantsTableActiveTabValue}
                setActiveTab={setParticipantsTableActiveTabValue}
                waitingListUpdateDialogIsOpen={waitingListUpdateDialogIsOpen}
                setWaitingListUpdateDialogIsOpen={setWaitingListUpdateDialogIsOpen}
              />
            </Grid>
            <SendEmailDialog
              isOpen={sendMessageDialogIsOpen}
              setIsOpen={setSendMessageDialogIsOpen}
              eventId={event.id}
            />
            <CheckinDialog
              order={order}
              isOpen={checkInDialogIsOpen}
              setIsOpen={setCheckInDialogIsOpen}
              updateItem={updateOrder}
            />
            <CheckinScannerDialog
              isOpen={checkInScannerOpen}
              setIsOpen={setCheckInScannerOpen}
              event={event}
              orders={ordersList}
              updateOrder={updateOrder}
            />
            <EditOrderDialog
              editOrderDialogState={{
                order,
                isOpen: editOrderIsOpen,
                orderPageId: orderPage.id,
                parentId: event.id,
                parent: event,
              }}
              closeEditOrderDialog={() => {
                setEditOrderIsOpen(false);
                setOrder(undefined);
              }}
              addOrder={async (orderToUpsert) => addOrder(orderToUpsert)}
              updateOrder={(orderToUpdate) => updateOrder(orderToUpdate)}
            />
            <EventActivityLog
              eventId={event.id}
              open={eventActivityLogOpen}
              onClose={() => setEventActivityLogOpen(false)}
            />
          </Grid>
        </ClickAwayListener>
      }
    />
  );
};

export default EventBoxDialog;
